/**
 * Cibles bulk imports — no React, no `t()`.
 *
 * Shared by the Cibles page (Targets.tsx) and any caller that has no dialog (onboarding).
 * Failures the UI must translate are thrown as `ImportError(key, params)`; API failures
 * propagate as `ApiError` so the caller keeps its own error mapping.
 */
import { TARGET_LIMITS } from '@traaker/shared';
import { api, ApiError } from '../../services/api';
import type { TargetRow } from './CATEGORIES';
import { ImportError, prepareImportBody, parseCatalogueCsv, gzipBytes } from './csv';

export type HarvestedUrl = { url: string; name?: string; id: string; type: 'produit' | 'collection' };

/** Staged NDJSON import: begin → PUT → commit. Throws ImportError / ApiError exactly as today. */
export async function stagedImport(brandId: string, body: Uint8Array, opts?: { mode?: 'merge' }): Promise<{ alreadyCommitted: boolean }> {
  // `declaredBytes` is the exact gzipped length, which the API signs into the
  // presigned PUT as ContentLength. Without it the URL is unpinned: anything up to
  // the worker's own cap can be staged under it for the whole TTL.
  let begun: { importId: string; uploadUrl: string; maxBytes?: number };
  try {
    begun = await api.post(`/brands/${brandId}/targets/import`, {
      action: 'begin',
      declaredBytes: body.byteLength,
    });
  } catch (e: any) {
    // 413 = declaredBytes over the staging cap. Our own copy, not the API's.
    if (e instanceof ApiError && e.status === 413) throw new ImportError('cibles.import.error_too_large');
    throw e;
  }
  const { importId, uploadUrl, maxBytes } = begun;
  // Belt and braces: reject before burning the presigned PUT rather than letting S3
  // fail the upload and leave the staging slot dangling.
  if (maxBytes && body.byteLength > maxBytes) {
    await api.post(`/brands/${brandId}/targets/import`, { action: 'abort', importId }).catch(() => { /* best effort */ });
    throw new ImportError('cibles.import.error_too_large');
  }

  const putResp = await fetch(uploadUrl, {
    method: 'PUT',
    body,
    headers: { 'Content-Type': 'application/x-ndjson' },
  });
  if (!putResp.ok) throw new ImportError('cibles.errors.upload', { status: putResp.status });

  // `mode: 'merge'` ADDS to the partitions. Without it the commit replaces the
  // produit/collection partitions and deletes everything a previous sitemap or CSV
  // had put there (fixed 2026-10-05).
  try {
    await api.post(`/brands/${brandId}/targets/import`, {
      action: 'commit', importId, ...(opts?.mode ? { mode: opts.mode } : {}),
    });
  } catch (e: any) {
    // A commit replay (double-click) is refused with 409 IMPORT_ALREADY_COMMITTED.
    // The import IS running, so this is information, not a failure.
    if (e instanceof ApiError && e.status === 409 && e.body?.code === 'IMPORT_ALREADY_COMMITTED') {
      return { alreadyCommitted: true };
    }
    throw e;
  }
  return { alreadyCommitted: false };
}

/** Rows harvested from a sitemap → merge import (today's SitemapImportDialog.handleImport body). */
export async function importHarvestedUrls(
  brandId: string,
  urls: HarvestedUrl[],
  opts?: { parentTargetId?: string; tags?: string[] },
): Promise<void> {
  const parentTargetId = opts?.parentTargetId;
  const tags = opts?.tags ?? [];
  const rows = urls.map((u) => {
    const uType = u.type ?? 'produit';
    const fields: Array<{ name: string; value: string }> = [{ name: 'id', value: u.id }];
    if (uType === 'produit') {
      if (u.name) fields.push({ name: 'name', value: u.name });
      fields.push({ name: 'url', value: u.url });
    } else {
      fields.push({ name: 'url', value: u.url });
      if (u.name) fields.push({ name: 'name', value: u.name });
    }
    // No `label` without a real name: the worker derives one, and in merge mode a
    // derived label must not overwrite the one already stored (2026-10-08).
    const row: Record<string, unknown> = {
      category: uType,
      ...(u.name ? { label: u.name } : {}),
      fields,
    };
    if (parentTargetId) row.parentTargetId = parentTargetId;
    if (tags.length) row._raw_tags = tags;
    return JSON.stringify(row);
  });
  const uploadBody = await gzipBytes(new TextEncoder().encode(rows.join('\n')));
  // Merge: this ADDS a sitemap's URLs. Server-side, existing rows the sitemap doesn't
  // mention are carried over and the ones it does keep their targetId.
  await stagedImport(brandId, uploadBody, { mode: 'merge' });
}

/** Domain → first discovered sitemap → harvest → importHarvestedUrls. For a caller with no dialog (onboarding).
 *  Resolves with what was imported; rejects with an Error whose message is user-presentable or an i18n key via ImportError. */
export async function importSitemapForDomain(brandId: string, domain: string): Promise<{ imported: number; truncated: boolean; sitemapUrl: string }> {
  const d = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '').trim();
  if (!d) throw new ImportError('cibles.sitemap.no_sitemaps');
  const found = await api.post(`/brands/${brandId}/targets/sitemap/discover`, { domain: d }) as {
    sitemapUrls: string[]; wafBlocked: boolean;
  };
  const sitemapUrl = found.sitemapUrls?.[0];
  if (!sitemapUrl) throw new ImportError(found.wafBlocked ? 'cibles.sitemap.waf_alert' : 'cibles.sitemap.no_sitemaps');
  const res = await api.post(`/brands/${brandId}/targets/sitemap/harvest`, { sitemapUrl }) as {
    urls: HarvestedUrl[]; truncated?: boolean;
  };
  if (!res.urls?.length) throw new ImportError('cibles.sitemap.no_sitemaps');
  await importHarvestedUrls(brandId, res.urls);
  return { imported: res.urls.length, truncated: !!res.truncated, sitemapUrl };
}

/** Load existing targets of `category` and return id_field → targetId map. */
async function loadExistingById(brandId: string, category: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  let cursor: string | null = null;
  for (let page = 0; page < 5; page++) {
    const params = new URLSearchParams({ category, limit: '200' });
    if (cursor) params.set('cursor', cursor);
    const data = await api.get(`/brands/${brandId}/targets?${params}`) as { items: TargetRow[]; nextCursor: string | null };
    for (const row of data.items) {
      const idField = row.fields.find((f) => f.name === 'id')?.value as string | undefined;
      if (idField) map.set(idField, row.targetId);
      // For collection: also index by URL as fallback merge key
      if (category === 'collection') {
        const urlField = row.fields.find((f) => f.name === 'url')?.value as string | undefined;
        if (urlField && !idField) map.set(urlField, row.targetId);
      }
    }
    if (!data.nextCursor) break;
    cursor = data.nextCursor;
  }
  return map;
}

/**
 * Produit/collection catalogue CSV (today's merge-aware direct POST branch).
 *
 * The bulk NDJSON import path mints a fresh targetId for every row, so re-importing the
 * same CSV would create duplicate Target rows for every product. Instead: parse the CSV,
 * load existing produit targetIds, include them in the POST so apply_delta reuses the
 * existing row and only ADDS new URL signals.
 *
 * `onQueued` fires as soon as a batch is queued (the page shows its notice mid-import).
 */
export async function importCatalogueCsv(
  brandId: string,
  file: File,
  marques: TargetRow[],
  opts?: { onQueued?: () => void },
): Promise<{ warnings: string[]; queued: boolean }> {
  const { produits, collections, errors, warnings } = await parseCatalogueCsv(file, marques);
  if (errors.length) {
    const shown = errors.slice(0, 10);
    const extra = errors.length > 10 ? `\n… et ${errors.length - 10} autre(s) erreur(s)` : '';
    throw new ImportError(shown.join('\n') + extra);
  }

  // The API requires each POST batch to target a single (ownerId, category) partition.
  // produit and collection must therefore be sent as separate requests — never mixed.
  const produitTargets: any[] = [];
  const collectionTargets: any[] = [];

  if (produits.length) {
    const existingProduits = await loadExistingById(brandId, 'produit');
    for (const g of produits) {
      const existingTid = existingProduits.get(g.id);
      const target: any = {
        ownerId: 'own', category: 'produit',
        label: g.name || g.id,
        parentTargetId: g.parentTargetId,
        fields: [
          { name: 'id', value: g.id },
          ...(g.name ? [{ name: 'name', value: g.name }] : []),
          ...g.urls.map((u) => ({ name: 'url', value: u.url, ...(u.buyingPage ? { buyingPage: true } : {}) })),
        ],
        tags: g.tags,
      };
      if (existingTid) target.targetId = existingTid;
      produitTargets.push(target);
    }
  }

  if (collections.length) {
    const existingCollections = await loadExistingById(brandId, 'collection');
    for (const g of collections) {
      const mergeKey = g.id || g.url;
      const existingTid = existingCollections.get(mergeKey);
      const fields: any[] = [];
      if (g.id) fields.push({ name: 'id', value: g.id });
      fields.push({ name: 'url', value: g.url });
      if (g.name) fields.push({ name: 'name', value: g.name });
      const target: any = {
        ownerId: 'own', category: 'collection',
        label: g.name || g.url,
        parentTargetId: g.parentTargetId,
        fields,
        tags: g.tags,
      };
      if (existingTid) target.targetId = existingTid;
      collectionTargets.push(target);
    }
  }

  // POST produits and collections as separate batches — the API partitions by
  // (ownerId, category) and refuses mixed-category requests.
  //
  // `matchByKey` (docs/features/targets.md §20.2, 2026-10-08): `loadExistingById` pages
  // in at most 5 × 200 rows, so on a larger catalogue the rest arrive WITHOUT their
  // targetId. The worker then matches them on their natural key and updates the stored
  // target — without it the stable-id 409 refused the batch half-way through an import
  // whose earlier batches were already written.
  let queued = false;
  for (const batch of [produitTargets, collectionTargets]) {
    for (let i = 0; i < batch.length; i += TARGET_LIMITS.maxBatchRows) {
      const resp = await api.post(`/brands/${brandId}/targets`, {
        targets: batch.slice(i, i + TARGET_LIMITS.maxBatchRows),
        matchByKey: true,
      }) as any;
      if (resp?.status === 'queued') { queued = true; opts?.onQueued?.(); }
    }
  }
  return { warnings, queued };
}

/** Any other category CSV (today's prepareImportBody + stagedImport branch, no merge). */
export async function importCategoryCsv(brandId: string, file: File, category: string, marques: TargetRow[]): Promise<{ warnings: string[]; alreadyCommitted: boolean }> {
  // Always gzipped bytes — never the raw File (see prepareImportBody).
  // marques needed by all categories that support a parentTargetId (contenu,
  // etablissement, personne) so marque_label → parentTargetId resolution works.
  const { body, warnings } = await prepareImportBody(file, category, marques);
  const { alreadyCommitted } = await stagedImport(brandId, body);
  return { warnings, alreadyCommitted };
}
